import {
  DEPENDENCY_SOURCES,
  type DependencySource,
} from "./workflowDependencies";

export type DependencyWorkflowInput = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  dependencies: ReadonlyArray<{
    name: string;
    version: string | null;
    source: string;
  }>;
};

type WorkflowDependency = {
  name: string;
  version: string | null;
  source: DependencySource;
};

export type WorkflowDependencyRow = {
  workflowId: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  dependencies: WorkflowDependency[];
};

export type DependencySummaryRow = {
  name: string;
  versions: string[];
  workflowCount: number;
  source: DependencySource;
};

export function computeWorkflowDependencies(
  workflows: ReadonlyArray<DependencyWorkflowInput>
): WorkflowDependencyRow[] {
  const rows: WorkflowDependencyRow[] = [];

  for (const workflow of workflows) {
    const commitDeps = workflow.dependencies;
    if (commitDeps.length === 0) continue;

    const deps: WorkflowDependency[] = [];
    const seen = new Set<string>();
    for (const source of DEPENDENCY_SOURCES) {
      for (const dep of commitDeps) {
        if (dep.source !== source) continue;
        const key = `${source}:${dep.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deps.push({ name: dep.name, version: dep.version, source });
      }
    }

    rows.push({
      workflowId: workflow.id,
      name: workflow.name,
      spaceId: workflow.spaceId,
      badgeSeed: workflow.badgeSeed,
      pausedAt: workflow.pausedAt,
      dependencies: deps,
    });
  }

  return rows.sort(
    (a, b) =>
      b.dependencies.length - a.dependencies.length ||
      a.name.localeCompare(b.name)
  );
}

export function workflowNamesByDependency(
  rows: ReadonlyArray<WorkflowDependencyRow>
): Map<string, string[]> {
  const byName = new Map<string, string[]>();
  for (const row of rows) {
    for (const dep of row.dependencies) {
      const names = byName.get(dep.name);
      if (names) names.push(row.name);
      else byName.set(dep.name, [row.name]);
    }
  }
  return byName;
}
